"use client";
import {
  TypePackageDetailItemSkeleton,
  TypePlanComparisonModule,
  isTypePackageDetailItem,
} from "@contentful/types";
import { faArrowRight } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { notEmpty } from "@yardzen/js-util";
import { Badge } from "@yardzen/ui/components/badge";
import { Button } from "@yardzen/ui/components/button";
import { H1 } from "@yardzen/ui/components/h1";
import { H2 } from "@yardzen/ui/components/h2";
import { Link } from "@yardzen/ui/components/link";
import { cn } from "@yardzen/ui/lib/utils";
import { Entry } from "contentful";
import { useSearchParams } from "next/navigation";
import { useState } from "react";
import { useBuilderPartnerCheckoutHref } from "./builderPartnerCheckoutHref";
import { ImageCollection } from "./ImageCollection";
import { PackageCard } from "./PackageCard";
import { RichText } from "./rich-text/RichText";
import { SelectorPills } from "./SelectorPills";

type PackageKey = "A" | "B" | "C" | "D";

export const PlanComparisonModule: React.FC<{
  planComparisonModule: TypePlanComparisonModule<"WITHOUT_UNRESOLVABLE_LINKS">;
}> = ({ planComparisonModule }) => {
  const { variant } = planComparisonModule?.fields || {};
  const queryParams = useSearchParams();
  const packageId = queryParams.get("option");

  // Map Contentful variant values to our internal constants
  const packageVariant = (() => {
    switch (variant) {
      case "double package":
        return "double";
      case "single package":
        return "single";
      case "premium":
        return "premium";
      default:
        return "single"; // fallback default
    }
  })();

  if (packageVariant === "premium") {
    return (
      <PremiumPackageComparisonModule
        planComparisonModule={planComparisonModule}
        packageId={packageId}
      />
    );
  } else {
    return (
      <MultiplePackageComparisonModule
        planComparisonModule={planComparisonModule}
        packageId={packageId}
      />
    );
  }
};

function MultiplePackageComparisonModule({
  planComparisonModule,
  packageId,
}: {
  planComparisonModule: TypePlanComparisonModule<"WITHOUT_UNRESOLVABLE_LINKS">;
  packageId: string | null;
}) {
  const {
    heading,
    subheading,
    moduleBanner,
    packageA,
    packagePlans,
    packageB,
    packagePlansB,
    packageC,
    packagePlansC,
    packageD,
    packagePlansD,
    buttonHeading,
    optionalCta,
    showListItems,
  } = planComparisonModule?.fields || {};

  // The four package slots are all optional in Contentful, so the pills render
  // only the filled ones and every index below addresses this list.
  const packageOptions = (
    [
      ["A", packageA],
      ["B", packageB],
      ["C", packageC],
      ["D", packageD],
    ] as const
  )
    .filter(([, label]) => Boolean(label))
    .map(([id, label]) => ({ id: id as PackageKey, label: label as string }));

  // Seeded from the first filled slot, not a hardcoded "A": an entry that
  // leaves A empty would otherwise open on A's missing plans.
  const [selectedPackage, setSelectedPackage] = useState<PackageKey>(
    packageOptions[0]?.id ?? "A",
  );
  const withBuilderPartnerParams = useBuilderPartnerCheckoutHref();

  // Determine which package plans to show based on selection
  const currentPackagePlans = (() => {
    switch (selectedPackage) {
      case "A":
        return packagePlans;
      case "B":
        return packagePlansB;
      case "C":
        return packagePlansC;
      case "D":
        return packagePlansD;
      default:
        return packagePlans;
    }
  })();

  // Ensure currentPackagePlans is always an array
  const currentPackagePlansArray = Array.isArray(currentPackagePlans)
    ? currentPackagePlans
    : currentPackagePlans
      ? [currentPackagePlans]
      : [];

  return (
    <div
      className={cn(
        "mt-5 flex flex-col items-center justify-center px-1 pb-8 pt-4 max-md:mt-4 md:px-8 md:pt-0",
      )}
    >
      <div className="flex w-full flex-col items-center justify-center px-4 py-5 md:px-0">
        {moduleBanner && (
          <div className="from-brand-tertiary-30 to-brand-tertiary-50 mb-0 hidden rounded-full bg-gradient-to-r px-6 py-2 shadow-lg sm:block">
            <RichText content={moduleBanner} pClassName="!mb-0 !text-center" />
          </div>
        )}
        <div className="w-full items-center justify-center text-center">
          {buttonHeading && (
            <Badge
              hover={false}
              className="bg-scheme-flag-bg text-scheme-flag-text mb-3 px-4 py-1.5 text-sm font-medium"
            >
              {buttonHeading}
            </Badge>
          )}
          <H1 className="!text-4xl md:!text-5xl">{heading}</H1>
          {subheading && (
            <div className="text-brand-primary-light mt-3 text-lg font-normal">
              {subheading}
            </div>
          )}
        </div>
        <div className="mt-5 flex w-full max-w-7xl flex-row items-center justify-center gap-4">
          <SelectorPills
            label="Yard type"
            // Indices address `packageOptions`, which drops the empty slots, so
            // they are mapped back through it rather than through PACKAGE_KEYS:
            // with B unset, the second pill is C, and PACKAGE_KEYS[1] would
            // have selected B and rendered its empty plans.
            selectedIndex={packageOptions.findIndex(
              (option) => option.id === selectedPackage,
            )}
            onSelect={(index) => setSelectedPackage(packageOptions[index].id)}
            options={packageOptions}
          />
        </div>

        <IndividualPackageModule
          packagePlans={currentPackagePlansArray}
          packageId={packageId}
          showListItems={showListItems}
        />
      </div>
      <div>
        {optionalCta && (
          <div className="mt-4 flex w-full flex-row items-center justify-center">
            <Button variant="outline" asChild>
              <Link
                href={withBuilderPartnerParams(`${optionalCta?.fields.url}`)}
              >
                {optionalCta?.fields.buttonText}{" "}
                <FontAwesomeIcon icon={faArrowRight} />
              </Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function IndividualPackageModule({
  packagePlans,
  packageId,
  showListItems,
}: {
  packagePlans?:
    | (
        | Entry<
            TypePackageDetailItemSkeleton,
            "WITHOUT_UNRESOLVABLE_LINKS",
            string
          >
        | undefined
      )[]
    | undefined;
  packageId: string | null;
  showListItems?: boolean;
}) {
  return (
    <div className="mt-8 flex w-full flex-col items-center justify-center gap-4 xl:flex-row">
      <div className="flex w-auto flex-col gap-4 md:flex-row">
        {packagePlans
          ?.filter(notEmpty)
          .filter(isTypePackageDetailItem)
          .map((packageCard) => (
            <PackageCard
              key={packageCard?.sys.id}
              keyVal={packageCard?.sys.id}
              package_card={packageCard}
              showImages={true}
              packageDetailPageVariant={false}
              packageId={packageId}
              cardFlagHidden={true}
              planComparisonModule={true}
              showListItems={showListItems}
            />
          ))}
      </div>
    </div>
  );
}

function PremiumPackageComparisonModule({
  planComparisonModule,
  packageId,
}: {
  planComparisonModule: TypePlanComparisonModule<"WITHOUT_UNRESOLVABLE_LINKS">;
  packageId: string | null;
}) {
  const { heading, packagePlans, packageCarousel, subheading, showListItems } =
    planComparisonModule?.fields || {};

  return (
    <div
      className={cn(
        "mt-5 flex flex-col items-center justify-center px-1 pb-8 pt-4 max-md:mt-4 md:px-8 md:pt-0",
      )}
    >
      <div className="flex w-full max-w-7xl flex-col items-center justify-center gap-4 px-4 py-5 md:px-0">
        <div className={cn("flex flex-row md:block")}>
          <div className="flex w-full flex-col">
            <H2 className="w-full">{heading}</H2>
            {subheading && <div>{subheading}</div>}
          </div>
        </div>
        <div
          className={cn(
            "flex w-full max-w-7xl flex-col items-start justify-center gap-4 md:flex-row md:items-stretch",
          )}
        >
          {packageCarousel && (
            <div className="w-full min-w-0 md:flex-1 md:basis-0">
              <ImageCollection
                imageCollection={packageCarousel}
                variant={"detail"}
                slideClassName="h-[220px] object-cover"
              />
            </div>
          )}
          {packagePlans
            ?.filter(notEmpty)
            .filter(isTypePackageDetailItem)
            .map((packageCard) => (
              <PackageCard
                key={packageCard?.sys.id}
                keyVal={packageCard?.sys.id}
                package_card={packageCard}
                showImages={false}
                packageDetailPageVariant={false}
                packageId={packageId}
                showListItems={showListItems}
              />
            ))}
        </div>
      </div>
    </div>
  );
}
